import {NavbarStateResourceNavbarDisplayModeEnum} from '@navbar-api';
import {useAvailabilityStore} from '@navbar-logged/features/availability/availability.store';
import {useLanguageSelectorStore} from '@navbar-logged/features/language-selector';
import {useNotificationsStore} from '@navbar-logged/features/notifications';
import {useProfilePreferencesStore, useUserStore} from '@navbar-logged/features/user';
import {callWithNuxt} from 'nuxt/app';
import {useNuxtApp} from '#imports';
import {FreelanceContext, FreelanceProfilePreferencesContext, NavbarContext} from './apis.registry';
import {useNavbarStateStore} from './navbar-state.store';

export function useAppContext() {
    const nuxtApp = useNuxtApp();
    const pinia = nuxtApp.$pinia;

    const navbarState = useNavbarStateStore(pinia);
    const userStore = useUserStore(pinia);
    const availabilityStore = useAvailabilityStore(pinia);
    const profilePreferencesStore = useProfilePreferencesStore(pinia);
    const notificationsStore = useNotificationsStore(pinia);
    const languageSelector = useLanguageSelectorStore(pinia);

    async function loadNavbarContext() {
        const {user, ...context} = await NavbarContext.getContext();
        if (context.state && navbarState.state.navbarDisplayMode === NavbarStateResourceNavbarDisplayModeEnum.Funnel) {
            context.state.navbarDisplayMode = NavbarStateResourceNavbarDisplayModeEnum.Funnel;
        }
        navbarState.$patch(context);
        if (user) {
            userStore.$patch({user: user});
        } else {
            const legacyUser = await NavbarContext.getUser();
            userStore.$patch({user: legacyUser});
        }
    }

    /** Load availability and preferences */
    async function loadAvailability() {
        if (userStore.hasSelectedFreelanceIdentity) {
            const [availability, preferences] = await Promise.allSettled([
                FreelanceContext.getAvailability(),
                FreelanceProfilePreferencesContext.getProfilePreferences(),
            ]);
            if (availability.status === 'fulfilled') {
                availabilityStore.availability = availability.value;
            }
            if (preferences.status === 'fulfilled') {
                profilePreferencesStore.preferences = preferences.value;
            }
        }
    }

    async function loadNotifications() {
        await notificationsStore.updateNotifications();
        if (import.meta.client) {
            notificationsStore.startUpdating();
        }
    }

    async function loadAppContext() {
        async function loadAllStates() {
            await Promise.allSettled([loadNavbarContext(), languageSelector.fetchAllLanguages()]);
            await callWithNuxt(nuxtApp, loadNotifications);
            await callWithNuxt(nuxtApp, () => languageSelector.redirectIfLgQueryPresent());
            await callWithNuxt(nuxtApp, loadAvailability);
        }

        try {
            navbarState.pending = true;
            await loadAllStates();
        } catch (e) {
            /* eslint-disable */
            console.error(`Navbar loader error:`, e);
            return null;
        } finally {
            navbarState.pending = false;
        }
    }

    return {
        loadNotifications,
        loadAppContext,
    };
}
